import { isCoachRuntimeServiceReadModel } from "@billiard/contracts";
import type { CoachServiceReplacementCommand, CoachServiceReplacementContext } from "./api";

export const COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY =
  "billiard.ops-web.coach-service-replacement-recovery.v1";

export type CoachServiceReplacementRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "RETRY_ALLOWED";

export interface CoachServiceReplacementRecovery extends CoachServiceReplacementContext {
  command: CoachServiceReplacementCommand;
  state: CoachServiceReplacementRecoveryState;
}

export type CoachServiceReplacementRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CoachServiceReplacementRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | { status: "STORAGE_UNAVAILABLE"; canRetry: false; pendingOutcomeMayExist: boolean };

export type CoachServiceReplacementResolution<TResult> =
  | { status: "EXECUTED"; result: TResult }
  | { status: "RETRY_ALLOWED"; recovery: CoachServiceReplacementRecovery }
  | { status: "FAIL_CLOSED" };

let pendingRecovery: CoachServiceReplacementRecovery | null = null;
let dispatchingRecovery: CoachServiceReplacementRecovery | null = null;
let hydrated = false;
let storageUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function contextIsValid(value: unknown): value is CoachServiceReplacementContext {
  return (
    isRecord(value) &&
    typeof value.tenantId === "string" &&
    value.tenantId.length > 0 &&
    typeof value.storeId === "string" &&
    value.storeId.length > 0 &&
    typeof value.actorId === "string" &&
    value.actorId.length > 0
  );
}

function commandIsValid(
  value: unknown,
  context: CoachServiceReplacementContext,
): value is CoachServiceReplacementCommand {
  if (!isRecord(value) || !isRecord(value.context) || !isRecord(value.request)) return false;
  return (
    value.context.tenantId === context.tenantId &&
    value.context.storeId === context.storeId &&
    value.context.actorId === context.actorId &&
    isCoachRuntimeServiceReadModel(value.baseline) &&
    value.baseline.status === "ACTIVE" &&
    value.baseline.startedAt !== null &&
    typeof value.request.replacementCoachId === "string" &&
    value.request.replacementCoachId.length > 0 &&
    value.request.replacementCoachId !== value.baseline.coachId &&
    value.request.expectedServiceVersion === value.baseline.version &&
    typeof value.request.reason === "string" &&
    value.request.reason === value.request.reason.trim() &&
    [...value.request.reason].length >= 2 &&
    [...value.request.reason].length <= 500 &&
    typeof value.request.effectiveAt === "string" &&
    typeof value.request.previewExpiresAt === "string" &&
    Date.parse(value.request.previewExpiresAt) - Date.parse(value.request.effectiveAt) ===
      5 * 60 * 1_000 &&
    [
      value.request.expectedEffectiveSeconds,
      value.request.expectedBilledMinutes,
      value.request.expectedServiceFeeFen,
      value.request.expectedCommissionBaseFen,
      value.request.expectedCommissionFen,
      value.request.replacementPriceFen,
    ].every((item) => typeof item === "number" && Number.isSafeInteger(item) && item >= 0) &&
    typeof value.request.replacementRuleVersion === "number" &&
    Number.isSafeInteger(value.request.replacementRuleVersion) &&
    value.request.replacementRuleVersion >= 1 &&
    typeof value.request.replacementRuleId === "string" &&
    value.request.replacementRuleId.length > 0 &&
    typeof value.request.replacementPricingToken === "string" &&
    /^[a-f0-9]{64}$/u.test(value.request.replacementPricingToken) &&
    typeof value.request.replacementToken === "string" &&
    /^[a-f0-9]{64}$/u.test(value.request.replacementToken) &&
    typeof value.request.idempotencyKey === "string" &&
    value.request.idempotencyKey.length >= 8 &&
    value.request.idempotencyKey.length <= 128
  );
}

function immutableRecovery(
  context: CoachServiceReplacementContext,
  command: CoachServiceReplacementCommand,
  state: CoachServiceReplacementRecoveryState,
): CoachServiceReplacementRecovery {
  const value = structuredClone({ ...context, command, state }) as CoachServiceReplacementRecovery;
  const freezeDeep = (item: unknown): void => {
    if (!item || typeof item !== "object" || Object.isFrozen(item)) return;
    for (const child of Object.values(item)) freezeDeep(child);
    Object.freeze(item);
  };
  freezeDeep(value);
  return value;
}

function parseStored(value: unknown): CoachServiceReplacementRecovery | null {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.recovery)) return null;
  const context = {
    tenantId: value.recovery.tenantId,
    storeId: value.recovery.storeId,
    actorId: value.recovery.actorId,
  };
  if (
    !contextIsValid(context) ||
    !commandIsValid(value.recovery.command, context) ||
    !["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(String(value.recovery.state))
  ) {
    return null;
  }
  return immutableRecovery(
    context,
    value.recovery.command,
    value.recovery.state as CoachServiceReplacementRecoveryState,
  );
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(
  left: CoachServiceReplacementRecovery,
  right: CoachServiceReplacementRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): CoachServiceReplacementRecovery | null {
  const encoded = storage.getItem(COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parseStored(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CoachServiceReplacementRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(
      COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    storageUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = parseStored(JSON.parse(encoded) as unknown);
    if (!stored) {
      storageUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) storageUnavailable = true;
  } catch {
    storageUnavailable = true;
  }
}

function contextMatches(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function unavailableView(): CoachServiceReplacementRecoveryView {
  return { status: "STORAGE_UNAVAILABLE", canRetry: false, pendingOutcomeMayExist };
}

export function coachServiceReplacementContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CoachServiceReplacementContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCoachServiceReplacementRecovery(
  context: CoachServiceReplacementContext,
): CoachServiceReplacementRecoveryView {
  hydrate();
  if (storageUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function claimCoachServiceReplacementRecovery(
  context: CoachServiceReplacementContext,
  command: CoachServiceReplacementCommand,
): CoachServiceReplacementRecoveryView {
  const current = readCoachServiceReplacementRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!commandIsValid(command, context)) {
    storageUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    storageUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  hydrate();
  if (storageUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      storageUnavailable = true;
      return false;
    }
    return true;
  } catch {
    storageUnavailable = true;
    return false;
  }
}

export function beginCoachServiceReplacementDispatch(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  if (dispatchingRecovery || recovery.state === "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCoachServiceReplacementDispatch(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCoachServiceReplacementOutcomeUnknown(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    storageUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCoachServiceReplacementRecovery(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) return false;
    try {
      storage.removeItem(COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY);
      if (storage.getItem(COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      storageUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function reconcileCoachServiceReplacementAuthority<TResult>(
  recovery: CoachServiceReplacementRecovery,
  context: CoachServiceReplacementContext,
  result: TResult | null,
): CoachServiceReplacementResolution<TResult> {
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  if (result !== null) {
    return clearCoachServiceReplacementRecovery(recovery, context)
      ? { status: "EXECUTED", result }
      : { status: "FAIL_CLOSED" };
  }
  const retryable = immutableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStored(retryable)) {
    storageUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export function resetCoachServiceReplacementRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(COACH_SERVICE_REPLACEMENT_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts from a fresh fail-closed state.
  }
}

export function resetCoachServiceReplacementRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
}
